// ============================================================
// ChatContextMenu — reusable right-click menu for chat + SocialHub
// ============================================================
//
// Bronze-styled Phaser context menu. Relation-aware: the action list is
// computed from the target's `SocialRelation` so the menu shows the right
// entries (e.g. "Add Friend" for strangers, "Remove Friend" for friends).
//
// Singleton — only one menu visible at a time. Clicking outside the menu
// or pressing Escape closes it.

import Phaser from 'phaser';
import type { GameMode, SocialRelation } from '@roe/shared';

const DEPTH = 300; // above SocialHub's 287 search-popup layer
const ROW_H = 22;
const MENU_W = 180;
const BG_COLOR = 0x0d0a0e;
const BORDER_COLOR = 0xc8973a;
const TEXT_COLOR = '#c8973a';
const HOVER_COLOR = '#ffe8a0';
const DANGER_COLOR = '#ef4444';

export interface ChatContextMenuCallbacks {
  onWhisper?: (userId: string, name: string) => void;
  onInvite?: (userId: string, name: string) => void;
  onAddFriend?: (userId: string, name: string) => void;
  onRemoveFriend?: (userId: string, name: string) => void;
  onAcceptRequest?: (userId: string, name: string) => void;
  onCancelRequest?: (userId: string, name: string) => void;
  onBlock?: (userId: string, name: string) => void;
  onUnblock?: (userId: string, name: string) => void;
}

export interface ChatContextMenuOpts {
  x: number;
  y: number;
  sceneWidth?: number;
  sceneHeight?: number;
  targetUserId: string;
  targetName: string;
  targetOnline?: boolean;
  relation?: SocialRelation;
  /** Source UI context — determines whether to show the MP Invite entry. */
  context: 'chat' | 'social-hub' | 'lobby';
  /** Lobby-specific: the MP mode the user is currently in. Only when context === 'lobby'. */
  lobbyMode?: GameMode;
}

interface ActionEntry {
  label: string;
  color?: string;
  fn: () => void;
  /** When truthy, clicking once shows a "Confirm?" label for 3s — a second
   *  click commits, otherwise the entry reverts. Used for Block. */
  confirm?: boolean;
}

/** Singleton handle — only one open menu at a time. */
let active: { destroy: () => void } | null = null;

export class ChatContextMenu {
  static open(
    scene: Phaser.Scene,
    opts: ChatContextMenuOpts,
    callbacks: ChatContextMenuCallbacks,
  ): { destroy: () => void } {
    // Close any existing menu first.
    active?.destroy();

    const relation: SocialRelation = opts.relation ?? 'none';
    const entries: ActionEntry[] = [];

    // Whisper — always available (context menu only surfaces for other users).
    entries.push({
      label: 'Whisper',
      fn: () => callbacks.onWhisper?.(opts.targetUserId, opts.targetName),
    });

    // Invite to multiplayer — only in lobby context, for online friends.
    if (opts.context === 'lobby' && relation === 'friend' && opts.targetOnline) {
      entries.push({
        label: 'Invite to Multiplayer',
        color: '#7acc60',
        fn: () => callbacks.onInvite?.(opts.targetUserId, opts.targetName),
      });
    }

    // Relation-specific entries.
    switch (relation) {
      case 'friend':
        entries.push({
          label: 'Remove Friend',
          fn: () => callbacks.onRemoveFriend?.(opts.targetUserId, opts.targetName),
        });
        break;
      case 'pending_sent':
        entries.push({
          label: 'Cancel Request',
          fn: () => callbacks.onCancelRequest?.(opts.targetUserId, opts.targetName),
        });
        break;
      case 'pending_received':
        entries.push({
          label: 'Accept Request',
          color: '#7acc60',
          fn: () => callbacks.onAcceptRequest?.(opts.targetUserId, opts.targetName),
        });
        break;
      case 'blocked':
        entries.push({
          label: 'Unblock',
          fn: () => callbacks.onUnblock?.(opts.targetUserId, opts.targetName),
        });
        break;
      case 'none':
      default:
        entries.push({
          label: 'Add Friend',
          fn: () => callbacks.onAddFriend?.(opts.targetUserId, opts.targetName),
        });
        break;
    }

    // Block is hidden when already blocked (Unblock already shown).
    if (relation !== 'blocked') {
      entries.push({
        label: 'Block',
        color: DANGER_COLOR,
        confirm: true,
        fn: () => callbacks.onBlock?.(opts.targetUserId, opts.targetName),
      });
    }

    // Compute menu dimensions + edge-clamp position.
    const menuH = entries.length * ROW_H + 4;
    const sceneW = opts.sceneWidth ?? 960;
    const sceneH = opts.sceneHeight ?? 540;
    const x = Math.min(Math.max(4, opts.x), sceneW - MENU_W - 4);
    const y = Math.min(Math.max(4, opts.y), sceneH - menuH - 4);

    const created: Phaser.GameObjects.GameObject[] = [];

    // Outer bg + border.
    const bg = scene.add.rectangle(x, y, MENU_W, menuH, BG_COLOR, 0.98)
      .setOrigin(0, 0).setStrokeStyle(1, BORDER_COLOR, 1).setDepth(DEPTH)
      .setInteractive({ useHandCursor: false });
    created.push(bg);

    // Scene-wide dismiss: clicking the background (not the menu) closes.
    const bgBlocker = scene.add.rectangle(sceneW / 2, sceneH / 2, sceneW, sceneH, 0x000000, 0.0)
      .setDepth(DEPTH - 1).setInteractive({ useHandCursor: false });
    bgBlocker.on('pointerdown', () => destroy());
    created.push(bgBlocker);

    const rowTexts: Phaser.GameObjects.Text[] = [];
    entries.forEach((entry, i) => {
      const rowY = y + 2 + i * ROW_H;
      const rowBg = scene.add.rectangle(x + 2, rowY, MENU_W - 4, ROW_H, BG_COLOR, 0.0)
        .setOrigin(0, 0).setDepth(DEPTH + 1).setInteractive({ useHandCursor: true });
      const label = scene.add.text(x + 10, rowY + ROW_H / 2, entry.label, {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: entry.color ?? TEXT_COLOR,
      }).setOrigin(0, 0.5).setDepth(DEPTH + 2);
      rowTexts.push(label);

      // Hover highlight.
      rowBg.on('pointerover', () => {
        rowBg.setFillStyle(BORDER_COLOR, 0.12);
        label.setColor(HOVER_COLOR);
      });
      rowBg.on('pointerout', () => {
        rowBg.setFillStyle(BG_COLOR, 0.0);
        label.setColor(entry.color ?? TEXT_COLOR);
      });

      let confirmTimer: Phaser.Time.TimerEvent | undefined;
      let awaitingConfirm = false;

      rowBg.on('pointerdown', () => {
        if (entry.confirm && !awaitingConfirm) {
          // First click — enter confirm state; label changes to "Confirm?" for 3s.
          awaitingConfirm = true;
          label.setText(`Confirm: ${entry.label}?`);
          confirmTimer = scene.time.delayedCall(3000, () => {
            awaitingConfirm = false;
            confirmTimer = undefined;
            label.setText(entry.label);
          });
          return;
        }
        confirmTimer?.destroy();
        try { entry.fn(); } catch (e) { console.error('[ChatContextMenu]', e); }
        destroy();
      });

      created.push(rowBg, label);
    });

    // Esc closes.
    const keyHandler = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') destroy();
    };
    window.addEventListener('keydown', keyHandler, true);

    const destroy = (): void => {
      if (active !== handle) {
        // Another menu already replaced us — nothing to do.
        return;
      }
      active = null;
      window.removeEventListener('keydown', keyHandler, true);
      for (const o of created) { try { o.destroy(); } catch { /* already destroyed */ } }
    };

    const handle = { destroy };
    active = handle;
    return handle;
  }

  /** Close any open menu. Safe to call when no menu is open. */
  static close(): void {
    active?.destroy();
    active = null;
  }
}
